import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dmClient } from '../../services/dmClient';
import usePageTitle from '../../hooks/usePageTitle';
import './caseBuilder.css';

const REFERENCE_TYPES = [
  'PAYER_POLICY',
  'LCD_NCD',
  'ADJUSTMENT_REMARK_CODES',
  'PROCEDURE_CODING',
  'DIAGNOSIS_CODING',
  'CONTRACT_FEE_SCHEDULE',
  'NCCI_PTP',
  'MUE',
];

const CLAIM_KEYS = [
  'insurance.payer_id',
  'insurance.plan.plan_id',
  'claim.date_of_service_start',
  'claim.claim_type',
  'claim.lines[].procedure.code',
  'claim.lines[].procedure.code_system',
  'claim.lines[].procedure.modifiers',
  'claim.lines[].units',
  'claim.lines[].adjustments[].carc',
  'claim.lines[].adjustments[].rarc_codes',
  'claim.diagnoses[].code',
  'claim.diagnoses[].code_system',
  'case_category',
  'claim.service_facility.place_of_service',
  'clinical_context.encounter.care_setting',
  'claim.ordering_provider.npi',
  'claim.lines[].order_ids',
  'claim.lines[].clinical_source_ids',
];

const EMPTY = {
  rule_key: '',
  enabled: true,
  sort_order: 100,
  emit: 'reference_set',
  applies_at: 'claim',
  reference_type: 'PAYER_POLICY',
  match_paths: [],
  match_when: {},
  source_config: {},
  strategy_template: {},
  company_owned: false,
};

function lines(value) {
  return String(value || '').split(/\r?\n|,/).map((item) => item.trim()).filter(Boolean);
}

function fromRule(rule) {
  const when = rule.match_when || {};
  const source = rule.source_config || {};
  const strategy = rule.strategy_template || {};
  return {
    ...rule,
    pathsText: (rule.match_paths || []).join('\n'),
    carcText: (when.carc || []).join(', '),
    rarcText: (when.rarc || []).join(', '),
    referenceId: source.reference_id || '',
    graphId: source.graph_id || '',
    lookupCategory: source.lookup_category || (source.lookup_categories || []).join(', '),
    applicability: source.applicability_status || '',
    loadedContent: source.loaded_content || '',
    strategyText: (strategy.strategy_ids || []).join(', '),
    processText: (strategy.process_map_ids || []).join(', '),
  };
}

export default function ReferenceRulesPage() {
  usePageTitle('Reference mapping');
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dmClient.get('/case-builder/reference-rules')
      .then((res) => setItems(res.data?.data?.items || []))
      .catch((err) => setError(err.response?.data?.error || 'Could not load reference mappings.'));
  }, []);

  function edit(rule, lockKey) {
    setMessage('');
    setError('');
    setDraft({ ...fromRule(rule), lockKey: Boolean(lockKey) });
  }

  function addPath(path) {
    if (!path || !draft) return;
    const current = lines(draft.pathsText);
    if (!current.includes(path)) current.push(path);
    setDraft({ ...draft, pathsText: current.join('\n') });
  }

  async function save(event) {
    event.preventDefault();
    if (!draft) return;
    setBusy(true);
    setError('');
    const source = {
      ...(draft.source_config || {}),
      reference_id: draft.referenceId || null,
      graph_id: draft.graphId || null,
      lookup_category: draft.lookupCategory || null,
      applicability_status: draft.applicability || null,
      loaded_content: draft.loadedContent || null,
    };
    const when = { ...(draft.match_when || {}) };
    const carc = lines(draft.carcText);
    const rarc = lines(draft.rarcText);
    if (carc.length) when.carc = carc;
    else delete when.carc;
    if (rarc.length) when.rarc = rarc;
    else delete when.rarc;
    const strategy = {
      ...(draft.strategy_template || {}),
      strategy_ids: lines(draft.strategyText),
      process_map_ids: lines(draft.processText),
    };
    try {
      const res = await dmClient.put('/case-builder/reference-rules', {
        rule_key: draft.rule_key,
        enabled: draft.enabled,
        sort_order: Number(draft.sort_order) || 100,
        emit: draft.emit,
        applies_at: draft.applies_at,
        reference_type: draft.reference_type,
        match_paths: lines(draft.pathsText),
        match_when: when,
        source_config: source,
        strategy_template: draft.emit === 'strategy_task' ? strategy : (draft.strategy_template || {}),
      });
      setItems(res.data?.data?.items || []);
      setDraft(null);
      setMessage('Mapping saved for your company.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save the mapping.');
    } finally {
      setBusy(false);
    }
  }

  async function resetCompany(ruleKey) {
    setBusy(true);
    setError('');
    try {
      const res = await dmClient.delete(`/case-builder/reference-rules/${encodeURIComponent(ruleKey)}`);
      setItems(res.data?.data?.items || []);
      setDraft(null);
      setMessage('Company override removed. The platform mapping is in effect.');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove the company mapping.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="cb-page">
      <header className="cb-head">
        <div>
          <h1>Reference mapping</h1>
          <p>
            <Link to="/case-builder">Case Builder</Link>
            {' · claim keys that select a reference source. Saving applies to your company.'}
          </p>
        </div>
        <button
          type="button"
          className="dm-btn dm-btn--primary"
          onClick={() => edit(EMPTY, false)}
        >
          New mapping
        </button>
      </header>

      {error ? <p className="cb-error">{error}</p> : null}
      {message ? <p className="cb-note">{message}</p> : null}

      <div className="cb-table-wrap">
        <table className="cb-table">
          <thead>
            <tr>
              <th>Rule</th>
              <th>Claim keys</th>
              <th>Reference</th>
              <th>Source</th>
              <th>Scope</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6}>No reference mappings.</td></tr>
            ) : items.map((item) => (
              <tr key={item.rule_key}>
                <td>
                  <div className="cb-strong">{item.rule_key}</div>
                  <div className="cb-sub">{item.enabled ? 'Enabled' : 'Disabled'} · {item.emit === 'strategy_task' ? 'Strategy' : 'Source'}</div>
                </td>
                <td>{(item.match_paths || []).join(', ') || '—'}</td>
                <td>{item.reference_type}</td>
                <td>{item.source_config?.reference_id || item.source_config?.graph_id || (item.strategy_template?.strategy_ids || []).join(', ') || '—'}</td>
                <td>{item.company_owned ? 'Company' : 'Platform'}</td>
                <td><div className="cb-actions"><button type="button" onClick={() => edit(item, true)}>Edit</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {draft ? (
        <form className="cb-panel" onSubmit={save}>
          <h2>{draft.company_owned ? 'Edit company mapping' : 'Edit mapping'}</h2>
          <div className="cb-grid">
            <label className="cb-field">
              Rule key
              <input value={draft.rule_key} onChange={(e) => setDraft({ ...draft, rule_key: e.target.value })} required disabled={draft.lockKey} />
            </label>
            <label className="cb-field">
              Sort
              <input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })} />
            </label>
            <label className="cb-field">
              Writes
              <select value={draft.emit} onChange={(e) => setDraft({ ...draft, emit: e.target.value })}>
                <option value="reference_set">Reference source</option>
                <option value="strategy_task">Strategy task</option>
              </select>
            </label>
            <label className="cb-field">
              Applies at
              <select value={draft.applies_at} onChange={(e) => setDraft({ ...draft, applies_at: e.target.value })}>
                <option value="claim">Claim</option>
                <option value="line">Denied line</option>
              </select>
            </label>
            <label className="cb-field">
              Reference type
              <input list="cb-ref-types" value={draft.reference_type} onChange={(e) => setDraft({ ...draft, reference_type: e.target.value })} required />
              <datalist id="cb-ref-types">
                {REFERENCE_TYPES.map((item) => <option key={item} value={item} />)}
              </datalist>
            </label>
            <label className="cb-check">
              <input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
              Enabled
            </label>
          </div>

          <label className="cb-field">
            Claim keys
            <textarea rows={5} value={draft.pathsText} onChange={(e) => setDraft({ ...draft, pathsText: e.target.value })} />
          </label>
          <label className="cb-field">
            Add a known key
            <select defaultValue="" onChange={(e) => { addPath(e.target.value); e.target.value = ''; }}>
              <option value="">Choose a claim path</option>
              {CLAIM_KEYS.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>

          <div className="cb-grid">
            <label className="cb-field">
              Reference id
              <input value={draft.referenceId} onChange={(e) => setDraft({ ...draft, referenceId: e.target.value })} />
            </label>
            <label className="cb-field">
              Graph id
              <input value={draft.graphId} onChange={(e) => setDraft({ ...draft, graphId: e.target.value })} />
            </label>
            <label className="cb-field">
              Lookup category
              <input value={draft.lookupCategory} onChange={(e) => setDraft({ ...draft, lookupCategory: e.target.value })} />
            </label>
            <label className="cb-field">
              Applicability
              <input value={draft.applicability} onChange={(e) => setDraft({ ...draft, applicability: e.target.value })} />
            </label>
            <label className="cb-field">
              Loaded content
              <input value={draft.loadedContent} onChange={(e) => setDraft({ ...draft, loadedContent: e.target.value })} />
            </label>
          </div>

          {draft.emit === 'strategy_task' ? (
            <div className="cb-grid">
              <label className="cb-field">
                CARC codes
                <input value={draft.carcText} onChange={(e) => setDraft({ ...draft, carcText: e.target.value })} placeholder="197, 50" />
              </label>
              <label className="cb-field">
                RARC codes
                <input value={draft.rarcText} onChange={(e) => setDraft({ ...draft, rarcText: e.target.value })} placeholder="N265" />
              </label>
              <label className="cb-field">
                Strategy ids
                <input value={draft.strategyText} onChange={(e) => setDraft({ ...draft, strategyText: e.target.value })} />
              </label>
              <label className="cb-field">
                Process map ids
                <input value={draft.processText} onChange={(e) => setDraft({ ...draft, processText: e.target.value })} />
              </label>
            </div>
          ) : null}

          <div className="cb-modal__actions">
            <button type="button" className="dm-btn dm-btn--secondary" onClick={() => setDraft(null)}>Cancel</button>
            {draft.company_owned ? (
              <button type="button" className="dm-btn dm-btn--ghost" onClick={() => resetCompany(draft.rule_key)} disabled={busy}>Use platform mapping</button>
            ) : null}
            <button type="submit" className="dm-btn dm-btn--primary" disabled={busy}>Save for company</button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
